import { useMemo, useState } from "react";
import { Check, Minus, Search, X } from "lucide-react";

import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Reveal } from "@/components/site/Reveal";
import type { PricingAddon, PricingFeatureGroup, PricingPlanRow } from "@/lib/pricing.types";
import { cn } from "@/lib/utils";
import { useT } from "@/lib/i18n-dict";
import { SHOW_PRICES } from "@/lib/pricing-visibility";

function Chip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        "inline-flex items-center gap-2 rounded-full border px-4 py-1.5 text-sm font-medium transition-colors",
        active
          ? "border-primary bg-primary/15 text-primary"
          : "border-border text-muted-foreground hover:border-primary/40 hover:text-foreground",
      )}
    >
      {children}
    </button>
  );
}

function Cell({ value, highlighted }: { value: string; highlighted?: boolean }) {
  const t = useT();
  if (value === "yes") {
    return (
      <span
        className={cn(
          "mx-auto grid size-6 place-items-center rounded-full bg-primary text-primary-foreground ring-1 ring-foreground/25",
          !highlighted && "opacity-90",
        )}
        role="img"
        aria-label={t("Included")}
      >
        <Check className="size-4" strokeWidth={3} aria-hidden />
      </span>
    );
  }
  if (value === "no") {
    return (
      <Minus className="mx-auto size-5 text-muted-foreground/70" aria-label={t("Not included")} />
    );
  }
  if (value === "soon") {
    return (
      <span className="inline-flex rounded-full border border-primary/40 px-2.5 py-0.5 text-xs font-semibold text-primary">
        {t("Soon")}
      </span>
    );
  }
  const display = !SHOW_PRICES && /€/.test(value) ? t("On request") : value;
  return <span className="text-sm text-muted-foreground md:text-base">{display}</span>;
}

/**
 * Interactive plan / add-on comparison matrix.
 * All values come from the CMS (pricing_plans, pricing_features,
 * pricing_addons + pricing_addon_plans); the UI only filters them.
 */
export function PricingMatrix({
  plans,
  groups,
  addons,
}: {
  plans: PricingPlanRow[];
  groups: PricingFeatureGroup[];
  addons: PricingAddon[];
}) {
  const [planIds, setPlanIds] = useState<string[]>([]);
  const [addonIds, setAddonIds] = useState<string[]>([]);
  const [query, setQuery] = useState("");
  const [diffOnly, setDiffOnly] = useState(false);
  const t = useT();

  const toggle = (list: string[], id: string) =>
    list.includes(id) ? list.filter((x) => x !== id) : [...list, id];

  /** Plans shown: explicit plan picks, then narrowed by add-on availability. */
  const visiblePlans = useMemo(() => {
    let out = planIds.length ? plans.filter((p) => planIds.includes(p.id)) : plans;
    if (addonIds.length) {
      out = out.filter((p) =>
        addonIds.every((aid) => addons.find((a) => a.id === aid)?.planIds.includes(p.id)),
      );
    }
    return out;
  }, [plans, planIds, addonIds, addons]);

  const q = query.trim().toLowerCase();

  const visibleGroups = useMemo(() => {
    return groups
      .map((group) => ({
        ...group,
        features: group.features.filter((f) => {
          if (q && !f.label.toLowerCase().includes(q) && !group.title.toLowerCase().includes(q)) {
            return false;
          }
          if (diffOnly && visiblePlans.length > 1) {
            const values = visiblePlans.map((p) => f.values[p.id] ?? "no");
            if (values.every((v) => v === values[0])) return false;
          }
          return true;
        }),
      }))
      .filter((g) => g.features.length > 0);
  }, [groups, q, diffOnly, visiblePlans]);

  const visibleAddons = useMemo(() => {
    let out = addonIds.length ? addons.filter((a) => addonIds.includes(a.id)) : addons;
    if (q) {
      out = out.filter(
        (a) =>
          a.name.toLowerCase().includes(q) ||
          a.tagline.toLowerCase().includes(q) ||
          a.description.toLowerCase().includes(q),
      );
    }
    return out;
  }, [addons, addonIds, q]);

  const hasFilters = planIds.length > 0 || addonIds.length > 0 || q.length > 0 || diffOnly;

  return (
    <Reveal className="mt-10 space-y-6">
      {/* Controls */}
      <div className="rounded-xl border border-border bg-surface p-5 md:p-6">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div className="relative w-full md:max-w-sm">
            <Search className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t("Search features or add-ons")}
              aria-label={t("Search features or add-ons")}
              className="pl-9"
            />
          </div>
          <label className="flex items-center gap-3 text-sm text-muted-foreground">
            <Switch
              checked={diffOnly}
              onCheckedChange={setDiffOnly}
              aria-label={t("Show differences only")}
            />
            {t("Show differences only")}
          </label>
        </div>

        <div className="mt-5 space-y-4">
          <div>
            <p className="mb-2 font-display text-xs font-semibold tracking-[0.18em] text-muted-foreground uppercase">
              {t("Plans")}
            </p>
            <div className="flex flex-wrap gap-2">
              {plans.map((plan) => (
                <Chip
                  key={plan.id}
                  active={planIds.includes(plan.id)}
                  onClick={() => setPlanIds((prev) => toggle(prev, plan.id))}
                >
                  {plan.name}
                </Chip>
              ))}
            </div>
          </div>
          {addons.length ? (
            <div>
              <p className="mb-2 font-display text-xs font-semibold tracking-[0.18em] text-muted-foreground uppercase">
                {t("Add-ons")}
              </p>
              <div className="flex flex-wrap gap-2">
                {addons.map((addon) => (
                  <Chip
                    key={addon.id}
                    active={addonIds.includes(addon.id)}
                    onClick={() => setAddonIds((prev) => toggle(prev, addon.id))}
                  >
                    {addon.name}
                  </Chip>
                ))}
              </div>
            </div>
          ) : null}
        </div>

        {hasFilters ? (
          <button
            type="button"
            onClick={() => {
              setPlanIds([]);
              setAddonIds([]);
              setQuery("");
              setDiffOnly(false);
            }}
            className="mt-5 inline-flex items-center gap-2 text-sm font-medium text-primary hover:underline"
          >
            <X className="size-4" /> {t("Reset filters")}
          </button>
        ) : null}
      </div>

      <Tabs defaultValue="features">
        <TabsList>
          <TabsTrigger value="features">{t("Plan features")}</TabsTrigger>
          <TabsTrigger value="addons">{t("Add-on availability")}</TabsTrigger>
        </TabsList>

        <TabsContent value="features" className="mt-6">
          {visiblePlans.length && visibleGroups.length ? (
            <MatrixTable
              corner={t("Feature")}
              plans={visiblePlans}
              rows={visibleGroups.flatMap((group) => [
                { type: "group" as const, id: group.id, label: group.title },
                ...group.features.map((f) => ({
                  type: "row" as const,
                  id: f.id,
                  label: f.label,
                  values: f.values,
                })),
              ])}
            />
          ) : (
            <EmptyMatrix />
          )}
        </TabsContent>

        <TabsContent value="addons" className="mt-6">
          {visiblePlans.length && visibleAddons.length ? (
            <MatrixTable
              corner={t("Add-on")}
              plans={visiblePlans}
              rows={visibleAddons.map((addon) => ({
                type: "row" as const,
                id: addon.id,
                label: addon.name,
                values: Object.fromEntries(
                  plans.map((p) => [p.id, addon.planIds.includes(p.id) ? "yes" : "no"]),
                ),
              }))}
            />
          ) : (
            <EmptyMatrix />
          )}
        </TabsContent>
      </Tabs>
    </Reveal>
  );
}

type MatrixRow =
  | { type: "group"; id: string; label: string }
  | { type: "row"; id: string; label: string; values: Record<string, string> };

function MatrixTable({
  corner,
  plans,
  rows,
}: {
  corner: string;
  plans: PricingPlanRow[];
  rows: MatrixRow[];
}) {
  return (
    <div className="overflow-x-auto rounded-xl border border-border bg-surface">
      <table className="w-full min-w-[640px] border-collapse text-left text-sm md:text-base">
        <thead>
          <tr className="border-b border-border">
            <th className="sticky left-0 z-10 bg-surface px-4 py-4 font-display text-sm font-semibold text-muted-foreground md:px-6">
              {corner}
            </th>
            {plans.map((plan) => (
              <th
                key={plan.id}
                className={cn(
                  "min-w-[120px] px-4 py-4 text-center font-display text-sm font-bold md:text-base",
                  plan.highlighted ? "text-primary" : "text-foreground",
                )}
              >
                {plan.name}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) =>
            row.type === "group" ? (
              <tr key={`g-${row.id}`} className="border-y border-border bg-background/60">
                <th
                  colSpan={plans.length + 1}
                  scope="colgroup"
                  className="sticky left-0 px-4 py-3 text-left font-display text-xs font-semibold tracking-[0.18em] text-primary uppercase md:px-6"
                >
                  {row.label}
                </th>
              </tr>
            ) : (
              <tr
                key={row.id}
                className="border-b border-border/60 transition-colors last:border-0 hover:bg-primary/5"
              >
                <th
                  scope="row"
                  className="sticky left-0 bg-surface px-4 py-3 text-left text-sm font-medium text-foreground md:px-6 md:text-base"
                >
                  {row.label}
                </th>
                {plans.map((plan) => (
                  <td key={plan.id} className="px-4 py-3 text-center">
                    <Cell value={row.values[plan.id] ?? "no"} highlighted={plan.highlighted} />
                  </td>
                ))}
              </tr>
            ),
          )}
        </tbody>
      </table>
    </div>
  );
}

function EmptyMatrix() {
  return (
    <div className="rounded-xl border border-dashed border-border p-10 text-center text-muted-foreground">
      Nothing matches these filters. Try clearing a plan or add-on selection.
    </div>
  );
}
